// Website templates. A template is a DESIGN: it supplies a renderer
// for every block type and draws it from the same config every other
// template reads. Switching templates therefore changes how a site
// looks and never what it says — every page, every block and every
// word survives, refitted into the new design.
//
// Mirrors App\Support\Website\WebsiteLayout::TEMPLATES. Adding one:
//   1. A folder beside Trailhead/ with a component for EVERY type in
//      WebsiteLayout::BLOCK_TYPES and an index.js mapping them (or,
//      like Backyard, spread another template's registry and draw
//      over the types that need a shape of their own).
//   2. An entry below, and its registry in templateRegistry.js and
//      siteComponents.js.
//   3. Its key in WebsiteLayout::TEMPLATES and its index in
//      SiteTemplateAssets::MODULES.
// sitetemplates_verify fails on a template missing a type, because a
// gap there is content that silently stops rendering after a switch.

//
// This file is the templates' DESCRIPTIONS only and imports none of
// their renderers, because a public page that imported all three
// downloaded all three: 317 scripts and stylesheets for a page drawn
// by one of them. The renderers are reached two ways:
//   - siteComponents.js, on a public page: loads the ONE template the
//     site uses, before the page renders (app.js, ssr.js);
//   - templateRegistry.js, in the builder: all of them, statically,
//     because the builder previews and switches between them.

import { hubPaletteVars } from '../../../lib/hubPalette.js';

export const TEMPLATES = {
    classic: {
        key: 'classic',
        label: 'Classic',
        description: 'Bold condensed headlines, dark bands and a fixed menu.',
        fonts: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@400;500;600;700&family=Barlow+Condensed:wght@400;500;600;700&display=swap',
        rootClass: 'tpl-classic',
        // Classic draws its own shapes; the corner setting is a
        // Trailhead control and is hidden while Classic is chosen.
        usesCorners: false,
        swatches: ['#1a1a1a', '#dd9b36', '#f8f8f8'],
    },
    trailhead: {
        key: 'trailhead',
        label: 'Trailhead',
        description: 'Light paper, wide headlines and status-first trail cards.',
        fonts: 'https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,400..800&family=Public+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap',
        rootClass: 'tpl-trailhead',
        family: 'trailhead',
        usesCorners: true,
        swatches: ['#f7f6f2', '#2f6a4a', '#1b1d1a'],
    },
    // Backyard is built ON Trailhead: the same renderers, re-drawn by
    // its own stylesheet, with its own menu, hero, quick links, stats
    // and footer. So it wears both classes and every editor control
    // that is about Trailhead's renderers (a forecast panel, the
    // colors a block reads) asks `family`, never the key.
    backyard: {
        key: 'backyard',
        label: 'Backyard',
        description: 'Chunky hand-drawn type, tilted cards and wavy color bands.',
        fonts: 'https://fonts.googleapis.com/css2?family=Londrina+Solid:wght@400;900&display=swap',
        rootClass: 'tpl-trailhead tpl-backyard',
        family: 'trailhead',
        usesCorners: true,
        swatches: ['#f4ede9', '#d3d65b', '#160e14'],
    },
    // Hub is the public hub page (/hub/{slug}) grown into a site, built
    // ON Trailhead like Backyard: same renderers, redrawn by hub.css,
    // with its own menu, hero, page header, stats and footer. Its colors
    // are DERIVED from the accent (lib/hubPalette.js), never picked.
    // The default for a new site, and the one template on every plan
    // (FREE_TEMPLATE in WebsiteLayout).
    hub: {
        key: 'hub',
        label: 'Hub',
        description: 'Your hub page as a whole site: a header in your color, white cards, status first.',
        fonts: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400..800&family=JetBrains+Mono:wght@400;500&display=swap',
        rootClass: 'tpl-trailhead tpl-hub',
        family: 'trailhead',
        // Hub's radii are the hub page's (10/16/18px) and do not follow
        // the Corners setting, so the control is hidden while it is chosen.
        usesCorners: false,
        swatches: ['#f4f6f4', '#906523', '#101a15'],
    },
    // Groundwork is built ON Trailhead like Backyard and Hub: the same
    // renderers, redrawn by groundwork.css, with its own menu, hero,
    // stats, page header and footer. A builder's spec sheet: condensed
    // headings, mono labels, ink rules. Drawn Sharp; follows Corners.
    groundwork: {
        key: 'groundwork',
        label: 'Groundwork',
        description: 'Condensed headlines, spec-sheet labels and ink rules. Made for trail builders.',
        fonts: 'https://fonts.googleapis.com/css2?family=Saira+Condensed:wght@500;600;700;800&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap',
        rootClass: 'tpl-trailhead tpl-groundwork',
        family: 'trailhead',
        usesCorners: true,
        swatches: ['#f1eee6', '#c4612d', '#1d1b17'],
    },
    // Ridgeline is built ON Trailhead too: the same renderers, redrawn
    // by ridgeline.css, with its own menu, hero, stats, page header and
    // footer, and full-bleed photo bands (Ridgeline/RlBand.vue) under the
    // hero, page header, newsletter, volunteer form, features grid and
    // call to action. Night ground, condensed uppercase headings, the
    // accent as a bright signal. Drawn Sharp; follows Corners.
    ridgeline: {
        key: 'ridgeline',
        label: 'Ridgeline',
        description: 'Full-bleed photo bands, tall condensed headlines and a bright accent on a dark ground.',
        fonts: 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
        rootClass: 'tpl-trailhead tpl-ridgeline',
        family: 'trailhead',
        usesCorners: true,
        swatches: ['#141714', '#acd379', '#f4f3ee'],
    },
};

export const DEFAULT_TEMPLATE = 'hub';

export const CORNERS = {
    sharp: { label: 'Sharp', radius: '0px' },
    soft: { label: 'Soft', radius: '6px' },
    round: { label: 'Round', radius: '18px' },
};

// Who the site is for (WebsiteLayout::AUDIENCES). Never a design: it
// picks the starter pages, menu and footer and the words a NEW block
// starts with (blocks.js defaultConfig), and no block reads it. The
// label is what a manager sees; "builder" is only the stored key.
export const AUDIENCES = {
    organization: { label: 'Organization', description: 'Trail conditions, events, volunteering and donations.' },
    builder: { label: 'Business', description: 'For a trail-building business: starts as one page with your name, where you are and how to reach you. Add starter pages for services, projects and a quote form.' },
};

export const DEFAULT_AUDIENCE = 'organization';

export function audienceFor(theme) {
    return AUDIENCES[theme?.audience] ? theme.audience : DEFAULT_AUDIENCE;
}

export function templateFor(theme) {
    return TEMPLATES[theme?.template] || TEMPLATES[DEFAULT_TEMPLATE];
}

// "Is this site drawn by Trailhead's renderers?" — true for Trailhead
// and for every template built on it. A check written as
// `key === 'trailhead'` hides a Trailhead control from Backyard while
// the block under it still reads that control.
export function usesTrailheadBlocks(key) {
    return TEMPLATES[key]?.family === 'trailhead';
}

export function rootClass(theme) {
    return templateFor(theme).rootClass;
}

// Inline variables on the site root. Every Trailhead block reads its
// colour and corners from these rather than from a prop, so a change
// in the editor repaints the whole canvas at once.
export function rootStyle(theme) {
    const accent = theme?.accent || '#dd9b36';
    const radius = (CORNERS[theme?.corners] || CORNERS.soft).radius;
    const base = {
        '--accent': accent,
        '--r': radius,
        '--tint': `color-mix(in oklch, ${accent} 9%, #f7f6f2)`,
        // The text that reads on the accent. Backyard sets its buttons
        // and badges in it: ink on a pale accent like its own lime,
        // white on a dark one, so a forest green still reads.
        '--on-accent': onAccent(accent),
        // The accent as TEXT on ink. A pale accent reads there as it is;
        // a dark one (most orgs' greens) all but vanishes, so Backyard's
        // headings on its ink bands fall back to its own lime.
        '--accent-on-ink': onAccent(accent) === '#ffffff' ? '#ebec9b' : accent,
    };
    // A Hub site's header band, inks, links and tint are the hub page's
    // own derivation from the brand color, each solved for AA where it
    // carries text. Same function DailyReportShow.vue paints with, so a
    // site and its hub page agree on the color to the hex.
    const key = templateFor(theme).key;
    if (key === 'hub') return { ...base, ...(hubPaletteVars(accent) || {}) };
    // Ridgeline sets the accent as text on BOTH grounds: its night bands
    // and its paper sections. A pale accent (the design's lime) reads on
    // the night as it is and needs darkening on paper; a dark one (most
    // orgs' greens) is the other way round. --on-accent already answers
    // which kind it is.
    if (key === 'ridgeline') {
        const pale = base['--on-accent'] !== '#ffffff';
        return {
            ...base,
            '--rl-accent-hi': pale ? accent : `color-mix(in oklch, ${accent} 50%, #fff)`,
            '--rl-accent-ink': pale ? `color-mix(in oklch, ${accent} 42%, #141714)` : accent,
        };
    }
    return base;
}

// WCAG relative luminance, and whichever of ink or white contrasts
// more with it. Exported for the check that holds it.
export function onAccent(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return '#160e14';
    const lin = (i) => {
        const c = parseInt(m[1].slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const L = 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
    // Contrast against white is 1.05 / (L + .05); against the ink
    // (#160e14, L ≈ .0055) it is (L + .05) / .0555.
    return 1.05 / (L + 0.05) >= (L + 0.05) / 0.0555 ? '#ffffff' : '#160e14';
}

export function fontsHref(theme) {
    return templateFor(theme).fonts;
}
